import React, { useState, useEffect, useMemo } from 'react';
import DataTable from './components/DataTable/DataTable';
import { NEW_SOURCES_COLUMNS } from './constants/tableColumns';
import { API_BASE_URL } from './config';
import './NewSources.css';

/**
 * NewSourcesTableView - Refactored New Sources using DataTable component
 * 
 * Sprint 20: Replaces custom table code with DataTable
 * Shows organizations from emails that aren't in the scraper yet
 */

export default function NewSourcesTableView() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNewSources();
  }, []);

  const fetchNewSources = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/email-sources-analysis`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new sources');
      }

      const data = await response.json();
      setSources(data.sources || []);
    } catch (err) {
      console.error('Error fetching new sources:', err);
      setError(err.message);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToScraper = async (source) => {
    const orgName = source.organization;
    const sampleLink = source.sample_link || "";
    const suggestedUrl = source.suggested_url || "";
    
    // Build helpful prompt message
    let promptMessage = `Enter the procurement/bidding website URL for ${orgName}:\n\n`;
    
    if (sampleLink) {
      promptMessage += `💡 HINT: Sample job link from email:\n${sampleLink}\n\n`;
    }
    
    if (suggestedUrl) {
      promptMessage += `Suggested base URL: ${suggestedUrl}\n\n`;
    }
    
    promptMessage += `Example URLs:
• https://cityname.bonfirehub.com/opportunities
• https://cityname.opengov.com/procurements
• https://cityname.net/bids.aspx

You can also Google: "${orgName} procurement bids"`;

    const confirmed = window.confirm(
      `Add "${orgName}" to your scraper?\n\n✅ ${source.job_count} jobs found\n🎯 ${(source.avg_confidence * 100).toFixed(0)}% AI confidence\n\nYou'll need to provide their procurement website URL.`
    );

    if (!confirmed) return;

    // Pre-fill with suggested URL if available
    const website = prompt(promptMessage, suggestedUrl);
    if (!website) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/add-email-source-to-cities`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organization: orgName,
          website_url: website
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add organization');
      }

      const result = await response.json();
      
      let successMessage = `✅ Success! ${orgName} has been added!\n\n`;
      if (result.configuration) {
        const config = result.configuration;
        successMessage += `🔍 Auto-Detection Results:\n`;
        successMessage += `• Platform: ${config.platform || 'Unknown'}\n`;
        successMessage += `• Row Selector: ${config.row_selector || 'N/A'}\n`;
        successMessage += `• Dynamic: ${config.is_dynamic ? 'Yes' : 'No'}\n`;
      }
      successMessage += `\n📍 State: ${result.state || 'Unknown'}\n`;
      successMessage += `\nJobs from ${orgName} will now appear in your RFQ list!`;
      
      alert(successMessage);
      fetchNewSources(); // Refresh the list
    } catch (err) {
      console.error('Error adding organization:', err);
      alert(`❌ Error: ${err.message}`);
    }
  };

  // Add action column to table
  const columnsWithActions = useMemo(() => [
    ...NEW_SOURCES_COLUMNS,
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-success"
          onClick={(e) => {
            e.stopPropagation();
            handleAddToScraper(row.original);
          }}
          title={row.original.sample_link ? `Sample job: ${row.original.sample_title}` : 'Add this organization to your scraper'}
        >
          ➕ Add to Scraper
        </button>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 150,
    },
  ], [sources]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading new RFQ sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <h2>Error</h2>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 new-sources-page" style={{ padding: '20px', maxWidth: '95vw', margin: '0 auto' }}>
      <div className="page-header mb-4">
        <h2>📧 New RFQ Sources from Email</h2>
        <p className="text-muted">
          Organizations that sent you RFQ emails but aren't in your scraper yet.
          Consider adding high-value sources to capture more opportunities!
        </p>
      </div>

      {sources.length === 0 ? (
        <div className="alert alert-info">
          <h4>No new sources found</h4>
          <p>
            All organizations from your email notifications are already in your scraper,
            or you haven't received any emails yet.
          </p>
          <p className="mb-0">
            Make sure the email watcher is running and check back after receiving some RFQ emails!
          </p>
        </div>
      ) : (
        <>
          <DataTable
            columns={columnsWithActions}
            data={sources}
            tableId="new-sources-table"
            enableRowSelection={false}
            enableColumnResizing={true}
            enableColumnOrdering={true}
            enableFilters={false}
            enablePagination={false}
            className="new-sources-table"
            defaultSorting={[{ id: 'job_count', desc: true }]} // Sort by job count (highest first)
          />

          <div className="alert alert-info mt-4">
            <h5>💡 Recommendation Criteria</h5>
            <ul className="mb-0">
              <li><strong>✅ Add Now:</strong> 3+ jobs with 80%+ confidence, or 5+ jobs total - Worth adding to your scraper!</li>
              <li><strong>⏳ Maybe Later:</strong> 2+ jobs with 90%+ confidence - Might be worth tracking</li>
              <li><strong>❌ Not Worth It:</strong> Low volume or low confidence - Probably not reliable</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

